import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { axiosInstance } from '../context/AuthContext';
import { FaChevronLeft, FaChevronRight, FaSlidersH } from 'react-icons/fa';

function FeaturesSection({ preview = false, onAuthRequired }) {
    const navigate = useNavigate();
    const [blogPosts, setBlogPosts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const previousButtonRef = useRef(null);
    const nextButtonRef = useRef(null);
    const paginationRef = useRef(null);

    useEffect(() => {
        const fetchLatestBlogs = async () => {
            try {
                setIsLoading(true);
                const params = new URLSearchParams({
                    page: '1',
                    limit: '6',
                    status: '1',
                });
                const response = await axiosInstance.get(`/blog?${params.toString()}`);
                setBlogPosts(response.data.blogs || []);
            } catch (error) {
                console.error('Error fetching latest blogs:', error);
                setBlogPosts([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchLatestBlogs();
    }, []);

    const handleProtectedAction = (action) => {
        if (preview) {
            onAuthRequired?.();
            return;
        }
        action();
    };

    return (
        <div id="articles" className="w-full min-h-screen bg-gradient-to-b from-white to-gray-50 relative overflow-hidden mb-12">
            <div className="w-full max-w-screen-xl mx-auto">
                <div className="text-center pt-16 pb-8 px-4">
                    <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                        Art Articles
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                        Discover the powerful tools and features that make Artization the ultimate platform 
                        for digital artists and gallery curators.
                    </p>
                </div>

                <div className="mx-auto w-full px-6 md:px-12 lg:px-16">
                    <div className="mb-2 flex w-full items-center justify-between gap-4 px-1 py-2">
                        <button
                            ref={previousButtonRef}
                            type="button"
                            aria-label="Previous article"
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 shadow-sm transition hover:border-gray-900 hover:bg-gray-900 hover:text-white"
                        >
                            <FaChevronLeft size={13} />
                        </button>
                        <div className="flex min-w-0 flex-1 items-center justify-center gap-3 text-gray-500">
                            <FaSlidersH size={13} aria-hidden="true" />
                            <div ref={paginationRef} className="art-articles-pagination !static flex min-h-4 items-center justify-center" />
                        </div>
                        <button
                            ref={nextButtonRef}
                            type="button"
                            aria-label="Next article"
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 shadow-sm transition hover:border-gray-900 hover:bg-gray-900 hover:text-white"
                        >
                            <FaChevronRight size={13} />
                        </button>
                    </div>
                    <Swiper
                        modules={[Navigation, Pagination, Autoplay]}
                        spaceBetween={24}
                        slidesPerView={1}
                        onBeforeInit={(swiper) => {
                            swiper.params.navigation.prevEl = previousButtonRef.current;
                            swiper.params.navigation.nextEl = nextButtonRef.current;
                            swiper.params.pagination.el = paginationRef.current;
                        }}
                        navigation={{
                            prevEl: previousButtonRef.current,
                            nextEl: nextButtonRef.current,
                        }}
                        pagination={{
                            el: paginationRef.current,
                            clickable: true,
                        }}
                        autoplay={{ delay: 4500, disableOnInteraction: false }}
                        breakpoints={{
                            768: { slidesPerView: 2 },
                            1280: { slidesPerView: 3 }
                        }}
                        className="!overflow-visible py-6 pb-14"
                    >
                        {isLoading ? (
                            Array.from({ length: 6 }).map((_, index) => (
                                <SwiperSlide key={`blog-skeleton-${index}`}>
                                    <div className="blog-card bg-white/80 backdrop-blur-sm border border-gray-200/60 rounded-2xl shadow-lg h-full flex flex-col">
                                        <div className="rounded-2xl overflow-hidden mb-6 border border-gray-100 bg-gray-100 h-48 md:h-56 animate-pulse"></div>
                                        <div className="h-5 bg-gray-100 rounded w-3/4 mb-3 animate-pulse"></div>
                                        <div className="h-4 bg-gray-100 rounded w-full mb-2 animate-pulse"></div>
                                        <div className="h-4 bg-gray-100 rounded w-2/3 mt-auto animate-pulse"></div>
                                    </div>
                                </SwiperSlide>
                            ))
                        ) : blogPosts.length === 0 ? (
                            <SwiperSlide>
                                <div className="blog-card bg-white/80 backdrop-blur-sm border border-gray-200/60 p-6 rounded-2xl shadow-lg h-full flex items-center justify-center text-gray-500">
                                    No blog posts available
                                </div>
                            </SwiperSlide>
                        ) : (
                            blogPosts.map((post) => (
                                <SwiperSlide key={post._id}>
                                    <div
                                        className="overflow-hidden blog-card bg-white/80 backdrop-blur-sm border border-gray-200/60 rounded-2xl shadow-lg h-full flex flex-col hover:shadow-xl transition-shadow duration-300 cursor-pointer"
                                        onClick={() => handleProtectedAction(() => navigate(`/blog/${post._id}`))}
                                    >
                                        <div className="overflow-hidden mb-6 border border-gray-100">
                                            <img
                                                src={post.featuredImage || "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1200&q=80"}
                                                alt={post.title}
                                                className="w-full h-48 md:h-56 object-cover"
                                                loading="lazy"
                                            />
                                        </div>

                                        <h3 className="text-xl font-semibold text-gray-900 mb-3 leading-snug px-4 min-h-[60px]">
                                            {post.title}
                                        </h3>

                                        {/* <div className="p-4 mt-auto flex items-center gap-2 text-blue-600 font-semibold text-sm bg-gradient-to-r from-white via-white to-white px-3 py-2  ">
                                            <FontAwesomeIcon icon={faLink} className="text-blue-500" />
                                            <span className="truncate">{`${window.location.origin}/blog/${post._id}`}</span>
                                        </div> */}
                                    </div>
                                </SwiperSlide>
                            ))
                        )}
                    </Swiper>
                </div>

                <div className="text-center mt-24 px-4">
                    <div className="mb-12 relative bg-white rounded-3xl p-12 text-gray-900 max-w-5xl mx-auto overflow-hidden border border-gray-200 shadow-xl">
                        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white"></div>
                        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50/50 rounded-full -translate-y-32 translate-x-32"></div>
                        <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-50/50 rounded-full translate-y-24 -translate-x-24"></div>
                        
                        <div className="relative z-10">
                            <h3 className="text-4xl md:text-5xl font-bold mb-6 leading-tight text-gray-900">
                                Ready to Transform Your Art Gallery?
                            </h3>
                            <p className="text-xl mb-8 text-gray-600 max-w-3xl mx-auto leading-relaxed">
                                Join artists and curators using Artization to understand engagement and improve gallery performance with data-driven insights.
                            </p>
                            <button 
                                className="bg-white text-gray-900 px-10 py-4 rounded-full font-bold text-lg hover:bg-stone-100 transition-all duration-300 shadow-md"
                                onClick={() => handleProtectedAction(() => navigate('/signup'))}
                            >
                                Get Started Today
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FeaturesSection;